/**
 * Styled popup content builder for markers.
 * Dark-themed HTML with affiliation accent colors, coordinates, and metadata.
 */

import type { MarkerData, MarkerDetail } from './marker-state';
import { getAffiliationColor } from '../marker-icons';
import { buildClassificationControls } from './classification-popup';

/** Base popup container styles */
const POPUP_CSS = [
  'font-family:Inter,system-ui,sans-serif',
  'font-size:12px',
  'line-height:1.6',
  'color:#e0e0e0',
  'background:rgba(15,17,23,0.95)',
  'border-radius:8px',
  'padding:0',
  'min-width:180px',
  'max-width:260px',
  'overflow:hidden',
  'box-shadow:0 4px 20px rgba(0,0,0,0.6)',
].join(';');

/** Header bar styles (uses accent color as left border) */
function headerCss(accentColor: string): string {
  return [
    `border-left:3px solid ${accentColor}`,
    'padding:8px 12px',
    'background:rgba(255,255,255,0.04)',
  ].join(';');
}

/** Body section styles */
const BODY_CSS = 'padding:6px 12px 10px 12px';

/** Detail row styles */
const ROW_CSS = 'display:flex;justify-content:space-between;padding:2px 0';
const LABEL_CSS = 'color:#888;font-size:11px';
const VALUE_CSS = 'color:#ccc;font-size:11px;text-align:right';

/** Build popup HTML for a contact marker */
function buildContactPopupHtml(data: MarkerData): string {
  const accent = getAffiliationColor(data.cotType);
  const rows: string[] = [];

  if (data.team || data.role) {
    const detail = [data.team, data.role].filter(Boolean).join(' / ');
    rows.push(row('Team/Role', detail));
  }

  rows.push(row('UID', truncate(data.uid, 20)));
  rows.push(row('Position', formatCoords(data.position)));

  if (data.lastSeen) {
    rows.push(row('Last Seen', formatLastSeen(data.lastSeen)));
  }

  return wrap(accent, data.callsign, data.cotType, rows.join(''));
}

/** Build popup HTML for a CoT/track marker (feed tracks get classification controls) */
function buildCotPopupHtml(data: MarkerData, withClassification = false): string {
  const accent = getAffiliationColor(data.cotType);
  const rows = [
    imageBlock(data.imageUrl),
    row('Type', data.cotType),
    row('Position', formatCoords(data.position)),
    detailRows(data.details),
  ];
  const extra = withClassification ? buildClassificationControls(data) : '';
  return wrap(accent, data.callsign, data.cotType, rows.join('') + extra);
}

/** Render a snapshot image (e.g. a live camera) at the top of the popup. */
function imageBlock(imageUrl: string | undefined): string {
  if (!imageUrl) return '';
  const style = 'width:100%;border-radius:4px;margin-bottom:6px;display:block;background:#000';
  return `<img src="${escapeHtml(imageUrl)}" style="${style}" alt="snapshot" loading="lazy"/>`;
}

/** Render declared display-field rows for a feed track (empty when none). */
function detailRows(details: MarkerDetail[] | undefined): string {
  if (!details || details.length === 0) return '';
  return details.map((detail) => row(detail.label, detail.value)).join('');
}

/** Whether a layer is an external feed track (eligible for classification). */
export function isFeedTrack(layer: string): boolean {
  return layer !== 'contact' && layer !== 'cot';
}

/** Build popup HTML based on marker layer */
export function buildPopupHtml(data: MarkerData): string {
  if (data.layer === 'contact') return buildContactPopupHtml(data);
  return buildCotPopupHtml(data, isFeedTrack(data.layer));
}

/** Wrap content in the styled popup shell */
function wrap(accent: string, callsign: string, cotType: string, body: string): string {
  const badge = affiliationBadge(cotType);
  return [
    `<div style="${POPUP_CSS}">`,
    `<div style="${headerCss(accent)}">`,
    `<div style="display:flex;align-items:center;gap:6px">`,
    badge,
    `<b style="font-size:13px;color:#fff">${escapeHtml(callsign)}</b>`,
    '</div>',
    '</div>',
    `<div style="${BODY_CSS}">${body}</div>`,
    '</div>',
  ].join('');
}

/** Small affiliation indicator dot */
function affiliationBadge(cotType: string): string {
  const color = getAffiliationColor(cotType);
  return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0"></span>`;
}

/** Build a key/value detail row */
function row(label: string, value: string): string {
  return `<div style="${ROW_CSS}"><span style="${LABEL_CSS}">${label}</span><span style="${VALUE_CSS}">${escapeHtml(value)}</span></div>`;
}

/** Format coordinates as DMS-lite (degrees with 4 decimal places) */
function formatCoords(pos: google.maps.LatLngLiteral): string {
  const lat = pos.lat.toFixed(4);
  const lng = pos.lng.toFixed(4);
  return `${lat}, ${lng}`;
}

/** Format an ISO timestamp as relative time */
function formatLastSeen(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  return `${Math.floor(diffSec / 3600)}h ago`;
}

/** Truncate a string to maxLen with ellipsis */
function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

/** Escape HTML entities (data-structure lookup, no regex) */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  };
  let result = '';
  for (const ch of text) result += map[ch] ?? ch;
  return result;
}
